import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "shared/services/api/axiosInstance";
import { trodoAnalyticsTheme as theme } from "features/analytics/studio/theme";
import { ISSUES_UI_ENABLED } from "shared/config/featureFlags";
import { formatTimeLocal } from "shared/components/chat/chatCellFormat";

/**
 * ChatEntityDrawer — generic right-side drawer for entities referenced in
 * chat answers via `trodo://entity/<kind>/<id>` links.
 *
 * Renders a quick summary fetched from the entity's API and a "Open full
 * view" navigation to the existing detail page so the user never loses the
 * chat session as they explore.
 *
 * Supported kinds: run, span, user, group, issue, cluster, session, event.
 */

const KIND_LABEL = {
  run:     "Agent run",
  span:    "Agent span",
  user:    "User",
  group:   "Group",
  issue:   "Issue",
  cluster: "Use-case cluster",
  session: "Session",
  event:   "Event",
};

/**
 * Entity kind → read endpoint.
 *
 * Four of these were wrong and every click on them returned 404:
 *   span    /agent-runs/spans/:id  → the router is mounted at /api/agent-spans
 *   user    /users/:id             → the mount is /api/user-identities
 *   group   /groups/:id            → the mount is /api/group-identities, and it
 *                                    keys on (groupKey, groupId)
 *   cluster /agent-runs/clusters/  → removed with the density-clustering stack
 *
 * Every other span fetch in the app already used /agent-spans/:id; this file
 * was the lone outlier. Keep it that way: there is a test asserting each path
 * here resolves to a real Express route.
 */
function fetchEntity(kind, id, teamId) {
  const tid = teamId != null ? teamId : null;
  const params = tid ? { team_id: tid, teamId: tid } : {};
  switch (kind) {
    case "run":
      return axiosInstance.get(`/agent-runs/${encodeURIComponent(id)}`, { params }).then((r) => r.data);
    case "span":
      return axiosInstance.get(`/agent-spans/${encodeURIComponent(id)}`, { params }).then((r) => r.data);
    case "user":
      return axiosInstance.get(`/user-identities/${encodeURIComponent(id)}`, { params }).then((r) => r.data);
    case "group":
      // The route is /:groupKey/:groupId. Chat only ever carries one id, so we
      // assume the same default key the "Open full view" link already assumes.
      return axiosInstance.get(`/group-identities/company/${encodeURIComponent(id)}`, { params }).then((r) => r.data);
    case "issue":
      if (!ISSUES_UI_ENABLED) return Promise.reject(new Error("Issues are currently unavailable."));
      return axiosInstance.get(`/issues-v6/${encodeURIComponent(id)}`, { params }).then((r) => r.data);
    case "session":
      return axiosInstance.get(`/sessions/${encodeURIComponent(id)}`, { params }).then((r) => r.data);
    case "event":
      return axiosInstance.get(`/events/${encodeURIComponent(id)}`, { params }).then((r) => r.data);
    default:
      return Promise.reject(new Error(`Unknown entity kind: ${kind}`));
  }
}

/**
 * "Open full view" target.
 *
 * `entity` is the already-fetched payload, so a span can route via its own run
 * — spans have no standalone page.
 *
 * The span case used to point at /:team/trace?spanId=…, but /:team/trace is a
 * LegacyTraceToRunsRedirect that sends you to the runs list and DROPS the query
 * string, so the button silently lost the span. The clusters page was removed
 * with the density-clustering stack and its route no longer exists at all.
 */
function fullRouteFor(kind, id, teamSlug, entity) {
  if (!teamSlug) return null;
  const slug = encodeURIComponent(teamSlug);
  const eid = encodeURIComponent(id);
  switch (kind) {
    case "run":     return `/${slug}/agent-analytics/runs?runId=${eid}`;
    case "span": {
      const runId = entity && (entity.run_id || entity.runId);
      return runId
        ? `/${slug}/agent-analytics/runs?runId=${encodeURIComponent(runId)}&spanId=${eid}`
        : `/${slug}/agent-analytics/traces?spanId=${eid}`;
    }
    case "user":    return `/${slug}/activity/users/${eid}`;
    case "group":   return `/${slug}/activity/groups/company/${eid}`;
    case "issue":   return ISSUES_UI_ENABLED ? `/${slug}/agent-analytics/issues?issue=${eid}` : null;
    case "session": return `/${slug}/activity/sessions?sessionId=${eid}`;
    case "event":   return `/${slug}/activity/events?eventId=${eid}`;
    default: return null;
  }
}

export { fullRouteFor };

export default function ChatEntityDrawer({
  open,
  kind,
  id,
  teamId,
  teamSlug,
  onClose,
  /** When set, "Open full view" calls this instead of navigating away (e.g. in-chat run trace overlay). */
  onOpenFullView,
}) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !kind || !id) return undefined;
    let cancelled = false;
    setLoading(true);
    setError("");
    setData(null);
    fetchEntity(kind, id, teamId)
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e) => {
        if (cancelled) return;
        setError(e?.response?.data?.message || e?.message || "Failed to load");
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, kind, id, teamId]);

  // Esc closes.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const goFull = useCallback(() => {
    if (typeof onOpenFullView === "function") {
      onOpenFullView(kind, id, data);
      onClose?.();
      return;
    }
    const route = fullRouteFor(kind, id, teamSlug, (data && (data[kind] || data)) || null);
    if (route) {
      navigate(route);
      onClose?.();
    }
  }, [kind, id, data, teamSlug, navigate, onClose, onOpenFullView]);

  if (!open) return null;

  return (
    <div
      aria-modal="true"
      role="dialog"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 60,
        pointerEvents: "auto",
      }}
    >
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: "absolute",
          inset: 0,
          background: "rgba(15,23,42,0.32)",
        }}
      />
      {/* Panel */}
      <div
        style={{
          position: "absolute",
          top: 0, right: 0, bottom: 0,
          width: "min(480px, 92vw)",
          background: "var(--surface-base, #fff)",
          borderLeft: `1px solid ${theme.border}`,
          boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          className="px-4 py-3 flex items-center justify-between border-b"
          style={{ borderColor: theme.border }}
        >
          <div>
            <div style={{ fontSize: 11, color: theme.textTertiary, textTransform: "uppercase", letterSpacing: 0.6 }}>
              {KIND_LABEL[kind] || kind}
            </div>
            <div className="font-semibold" style={{ color: theme.textPrimary, fontSize: 14 }}>
              {data ? renderTitle(kind, data) : id}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-md px-2 py-1 hover:bg-surface-hover"
            style={{ color: theme.textSecondary, fontSize: 16 }}
          >
            ×
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-3">
          {loading && (
            <div className="text-xs" style={{ color: theme.textSecondary }}>Loading {kind} {id}…</div>
          )}
          {error && (
            <div className="text-xs" style={{ color: "rgb(239,68,68)" }}>
              {error}
            </div>
          )}
          {data && (
            <EntityBody kind={kind} data={data} />
          )}
        </div>

        <div className="px-4 py-3 border-t flex justify-end gap-2" style={{ borderColor: theme.border }}>
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 rounded-md text-sm"
            style={{ color: theme.textSecondary, border: `1px solid ${theme.border}` }}
          >
            Close
          </button>
          <button
            type="button"
            onClick={goFull}
            disabled={!onOpenFullView && !fullRouteFor(kind, id, teamSlug)}
            className="px-3 py-1.5 rounded-md text-sm font-medium"
            style={{
              backgroundColor: theme.primary,
              color: "var(--surface-base, #fff)",
              opacity: (onOpenFullView || fullRouteFor(kind, id, teamSlug)) ? 1 : 0.4,
            }}
          >
            Open full view
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Pull the entity out of whatever envelope its route wraps it in.
 *
 * These routes are not consistent: agent-spans returns {span}, and
 * user-identities returns {success, userDetails} — NOT {user}. The unwrap
 * below used to live only in EntityBody and only knew `user`, so a user
 * drawer fell through to the envelope itself and rendered "No summary fields
 * available" over a response that was full of them. renderTitle never
 * unwrapped at all, which is why the header just said "User".
 *
 * Nobody noticed because ChatPage navigated users away to their profile page
 * instead of ever opening this drawer. That carve-out is gone (Akshit, 3 Sep:
 * everything opens in place), so this path is live now.
 */
function entityOf(kind, data) {
  if (!data) return null;
  return data[kind]
    || data.userDetails || data.user || data.run || data.span || data.group
    || data.issue || data.session || data.event
    || data;
}

function renderTitle(kind, raw) {
  const data = entityOf(kind, raw);
  if (!data) return null;
  if (kind === "user") return data.email || data.distinct_id || data.distinctId || "User";
  if (kind === "group") return data.group_key || data.name || "Group";
  if (kind === "issue") return data.title || data.name || data.issue_id || "Issue";
  if (kind === "cluster") return data.name || data.cluster_label || data.cluster_id || "Cluster";
  if (kind === "run") return data.agent_name || data.run_id || "Run";
  if (kind === "span") return data.span_name || data.name || data.span_id || "Span";
  if (kind === "session") return data.session_id || "Session";
  if (kind === "event") return data.name || data.event_id || "Event";
  return null;
}

function EntityBody({ kind, data }) {
  // Show a compact key-value list of the most useful fields. The full
  // detail view is one click away via "Open full view".
  const rows = [];
  // A timestamp reads in the viewer's own clock here too, same as the tables
  // (Akshit, 3 Sep: convert to local, drop the UTC) — this drawer was still
  // printing raw "2026-05-01T10:08:38.766Z".
  const ISO = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/;
  const push = (label, value) => {
    if (value == null || value === "" || (Array.isArray(value) && !value.length)) return;
    if (Array.isArray(value)) return rows.push([label, value.join(", ")]);
    const str = String(value);
    rows.push([label, ISO.test(str) ? formatTimeLocal(str) : str]);
  };

  // Endpoints wrap the entity under its own kind — { span: {...} }, { run: {...} }.
  // This was a hand-listed OR-chain that simply omitted `span`, so a span
  // response fell through to the raw envelope and every field read undefined:
  // the drawer loaded fine and rendered "No summary fields available".
  // Keying off `kind` first means a new kind cannot be forgotten here.
  const obj = entityOf(kind, data) || {};

  if (kind === "user") {
    // Field names follow what /user-identities actually returns (camelCase,
    // location rather than country, email only inside custom_properties) —
    // the snake_case fallbacks are kept for any caller that passes a row.
    push("User ID", obj.distinctId || obj.distinct_id);
    push("Email", obj.email || obj.custom_properties?.email);
    push("Sessions", obj.total_sessions || obj.totalSessions);
    push("First visit", obj.first_visit || obj.firstVisit);
    push("Last visit", obj.last_visit || obj.lastVisit);
    push("Location", obj.country || obj.lastLocation || obj.firstLocation);
    push("Device", obj.device_type || obj.deviceType || obj.device);
    push("Browser", obj.browser);
  } else if (kind === "run") {
    push("Run ID", obj.run_id || obj.id);
    push("Agent", obj.agent_name || obj.agentName);
    push("Status", obj.status || obj.outcome);
    push("Tokens", obj.total_tokens ?? obj.tokens);
    push("Cost", obj.cost_usd != null ? `$${Number(obj.cost_usd).toFixed(4)}` : null);
    push("Duration", obj.duration_ms ? `${obj.duration_ms} ms` : null);
    push("Started", obj.started_at || obj.timestamp);
  } else if (kind === "issue") {
    push("Issue ID", obj.id);
    push("Status", obj.status);
    push("Agent", obj.agent_name);
    push("Source", obj.source);
    push("Evidence", obj.evidence_count);
    push("First seen", obj.first_seen_at);
    push("Last seen", obj.last_seen_at);
    if (obj.root_cause) push("Root cause", obj.root_cause);
    if (obj.solution) push("Solution", obj.solution);
  } else if (kind === "cluster") {
    push("Cluster ID", obj.cluster_id || obj.id);
    push("Label", obj.name || obj.label);
    push("Runs", obj.run_count || obj.total_runs);
    push("Success rate", obj.success_rate != null ? `${Math.round(obj.success_rate * 100)}%` : null);
    if (obj.description) push("Description", obj.description);
  } else if (kind === "span") {
    push("Span ID", obj.span_id || obj.id);
    push("Name", obj.name || obj.span_name);
    push("Kind", obj.kind);
    push("Status", obj.status);
    push("Duration", obj.duration_ms ? `${obj.duration_ms} ms` : null);
    if (obj.run_id) push("Parent run", obj.run_id);
  } else if (kind === "group") {
    push("Group key", obj.group_key);
    push("Group ID", obj.group_id || obj.id);
    push("Members", obj.member_count);
    push("Total events", obj.total_events);
  } else if (kind === "session") {
    push("Session ID", obj.session_id || obj.id);
    push("User", obj.distinctId || obj.distinct_id);
    push("Pages", obj.page_views || obj.pages);
    push("Duration", obj.duration_seconds ? `${obj.duration_seconds}s` : null);
    push("Started", obj.started_at);
  } else if (kind === "event") {
    push("Event ID", obj.event_id || obj.id);
    push("Name", obj.name || obj.event_name);
    push("User", obj.distinctId || obj.distinct_id);
    push("Time", obj.timestamp || obj.created_at);
    push("URL", obj.url || obj.page_url);
  }

  return (
    <div className="text-sm space-y-1">
      {rows.length === 0 ? (
        <div style={{ color: theme.textSecondary, fontSize: 12 }}>
          No summary fields available. Click "Open full view" for details.
        </div>
      ) : (
        rows.map(([label, value]) => (
          <div
            key={label}
            className="flex items-baseline gap-2 py-1"
            style={{ borderBottom: `1px solid ${theme.borderMuted || theme.border}` }}
          >
            <div style={{ fontSize: 11, color: theme.textSecondary, minWidth: 110 }}>{label}</div>
            <div style={{ fontSize: 12, color: theme.textPrimary, wordBreak: "break-word", flex: 1 }}>
              {value}
            </div>
          </div>
        ))
      )}
    </div>
  );
}
